import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import BucketLock from "@/services/bucket/bucket-lock";
import { BucketLockStatusResponse } from "@/dtos/BucketLock.dto";

export interface BucketLockStatusParams {
  organizationId: string;
  userId?: string;
  offset?: number;
}

export function useBucketLockStatus(
  params: Ref<BucketLockStatusParams | null> | BucketLockStatusParams | null
) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["bucketLockStatus", "disabled"];
    return ["bucketLockStatus", p.organizationId, p.userId || "self"];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async (): Promise<BucketLockStatusResponse> => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const result = await BucketLock.getBucketLockStatus({
        userId: p.userId,
        offset: p.offset ?? new Date().getTimezoneOffset(),
      });

      let data = null;
      if (result.data?.status && result.data?.data) {
        data = result.data.data;
      } else if (result.data && !result.data.status) {
        data = result.data;
      }

      if (data) {
        return {
          enabled: data.enabled ?? false,
          bucketStatuses: data.bucketStatuses ?? [],
        };
      }

      return {
        enabled: false,
        bucketStatuses: [],
      };
    },
    enabled,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
